import { Ellipsis, Icon } from '@advanza/ui'
import clsx from 'clsx'
import React from 'react'

interface ScoreBlockProps {
    name: string
    value: number
    lowerIsBetter?: boolean
    loading?: boolean
    highlight?: boolean
    altStyle?: boolean
    isPercentage?: boolean
    prevValue?: number | string | null
}

const ScoreBlock: React.FC<ScoreBlockProps> = ({
    name,
    value,
    lowerIsBetter,
    loading,
    highlight,
    altStyle,
    isPercentage,
    prevValue = null,
}) => {
    const previousValue = prevValue === null ? NaN : parseFloat(String(prevValue))
    const hasPreviousValue = !isNaN(previousValue)
    const isBetter = (() => {
        if (hasPreviousValue) {
            return lowerIsBetter ? value < previousValue : value > previousValue
        }
        return false
    })()
    const noDiff = hasPreviousValue && previousValue === value
    const diff = hasPreviousValue ? Math.round(value - previousValue) : 0
    const displayValue = isPercentage ? Math.round(value) + '%' : value

    return (
        <div
            className={clsx(
                'bg-white border border-grey-500 rounded-lg p-4 mb-0 shadow-[0_1px_3px_rgba(0,0,0,0.05)] transition-all duration-200 hover:shadow-[0_2px_8px_rgba(0,0,0,0.1)] hover:-translate-y-0.5',
                highlight && 'border-b-4 border-b-primary-600 font-bold rounded-b-[3px]',
                altStyle && 'bg-bg',
                loading && 'opacity-60',
            )}>
            <div className="text-grey-800 text-[13px] font-medium mb-1">
                <Ellipsis>{name}</Ellipsis>
            </div>
            <b className="text-2xl my-1.5 block font-semibold text-bodyDark">
                {loading ? '...' : displayValue}
            </b>
            {prevValue !== null && (
                <div className="text-grey-700 text-md opacity-70">
                    <div
                        className={clsx(
                            'inline-flex items-center gap-1',
                            noDiff && 'text-grey-700 opacity-70',
                            isBetter && !noDiff && 'text-active-green [&_*]:text-active-green',
                            !isBetter && !noDiff && 'text-churned-red [&_*]:text-churned-red',
                        )}>
                        {!noDiff && (
                            <Icon
                                fontSize={18}
                                name={isBetter ? 'arrow_upward' : 'arrow_downward'}
                            />
                        )}
                        <span>{diff}</span>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ScoreBlock
